import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "../config";
import { TrendingUp, Clock, Tag, ArrowLeft, Globe, Target } from "lucide-react";
import NotFoundPage from "./NotFoundPage";

const PRODUCTION_ORIGIN = "https://toprankpulse.com";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setError(false);
    setPost(null);
    axios
      .get(`${API}/blog/post/${slug}`)
      .then((res) => (res.data?.moved_to ? navigate(`/blog/${res.data.moved_to}`, { replace: true }) : setPost(res.data)))
      .catch((err) => {
        // V231 — Missing / deleted article → render NotFoundPage so
        // crawlers see <meta robots="noindex"> instead of a permanent
        // spinner (which Google interprets as Soft 404).
        const status = err?.response?.status;
        if (status === 404 || status === 410 || !err?.response) {
          setError(true);
        } else {
          console.error(err);
        }
      });
  }, [slug, navigate]);

  // V231 — Explicit 404 state for missing posts.
  if (error) return <NotFoundPage />;

  if (!post) return (
    <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#a855f7] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const translations = post.available_translations || [];

  // Build hreflang map: current post + every translation, keyed by language.
  const localeSlugs = {};
  localeSlugs[(post.language || "en").toLowerCase()] = post.slug;
  for (const tr of translations) {
    const lang = (tr.language || "").toLowerCase();
    if (lang && tr.slug && !localeSlugs[lang]) localeSlugs[lang] = tr.slug;
  }
  const xDefaultSlug = localeSlugs.en || post.slug;
  const canonical = `${PRODUCTION_ORIGIN}/blog/${post.slug}`;
  const lang = (post.language || "en").toLowerCase();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description || "",
    inLanguage: lang,
    keywords: (post.tags || []).join(", "),
    datePublished: post.published_at,
    dateModified: post.published_at,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    url: canonical,
    author: {
      "@type": "Organization",
      name: "TopRankPulse",
      url: PRODUCTION_ORIGIN,
    },
    publisher: {
      "@type": "Organization",
      name: "TopRankPulse",
      url: PRODUCTION_ORIGIN,
      logo: {
        "@type": "ImageObject",
        url: `${PRODUCTION_ORIGIN}/og-image.png`,
      },
    },
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <Helmet>
        <html lang={lang} />
        <title>{post.title}</title>
        {post.meta_description && (
          <meta name="description" content={post.meta_description} />
        )}
        <link rel="canonical" href={canonical} />

        {/* hreflang alternates — one per available translation + x-default */}
        {Object.entries(localeSlugs).map(([alt, altSlug]) => (
          <link
            key={alt}
            rel="alternate"
            hrefLang={alt}
            href={`${PRODUCTION_ORIGIN}/blog/${altSlug}`}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${PRODUCTION_ORIGIN}/blog/${xDefaultSlug}`}
        />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        {post.meta_description && (
          <meta property="og:description" content={post.meta_description} />
        )}
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={lang} />
        <meta property="og:site_name" content="TopRankPulse" />
        {/* V232.4 — If this is a Jendrál post with a Fal.ai-generated
            OG image, use that instead of the generic default.
            NB: react-helmet-async does NOT accept React Fragments as
            children — each conditional meta tag must be a direct
            sibling expression inside <Helmet>. */}
        <meta
          property="og:image"
          content={
            post.og_image_url
              ? (post.og_image_url.startsWith("http")
                  ? post.og_image_url
                  : `${PRODUCTION_ORIGIN}${post.og_image_url}`)
              : `${PRODUCTION_ORIGIN}/og-image.png`
          }
        />
        {post.og_image_url && (
          <meta property="og:image:width" content={String(post.og_image_width || 1200)} />
        )}
        {post.og_image_url && (
          <meta property="og:image:height" content={String(post.og_image_height || 630)} />
        )}
        {post.og_image_url && (
          <meta property="og:image:type" content="image/jpeg" />
        )}
        {post.og_image_url && (
          <meta
            name="twitter:image"
            content={
              post.og_image_url.startsWith("http")
                ? post.og_image_url
                : `${PRODUCTION_ORIGIN}${post.og_image_url}`
            }
          />
        )}
        {Object.keys(localeSlugs)
          .filter((l) => l !== lang)
          .map((l) => (
            <meta key={l} property="og:locale:alternate" content={l} />
          ))}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        {post.meta_description && (
          <meta name="twitter:description" content={post.meta_description} />
        )}

        {/* JSON-LD Article schema */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <a href="/blog" className="flex items-center gap-2 text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors text-sm font-['JetBrains_Mono']">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </a>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12">
        {/* Language flag + category */}
        <div className="flex items-center gap-3 mb-3">
          <span className="text-2xl" title={post.language_name}>{post.flag || "🇬🇧"}</span>
          <span className="text-[#a855f7] font-['JetBrains_Mono'] text-sm uppercase tracking-wider">{post.category}</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4" data-testid="post-title">{post.title}</h1>

        <div className="flex items-center gap-4 text-[var(--color-text-muted)] text-sm mb-6">
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{new Date(post.published_at).toLocaleDateString()}</span>
          <span className="flex items-center gap-1"><Target className="w-4 h-4" />{post.target_keyword}</span>
          <span className="flex items-center gap-1"><Globe className="w-4 h-4" />{post.word_count} words</span>
          <span className="font-bold text-emerald-400 font-['JetBrains_Mono']">SEO: {post.seo_score}/100</span>
        </div>

        {/* Available translations */}
        {translations.length > 0 && (
          <div className="mb-8 pb-6 border-b border-[var(--color-border)]">
            <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-2">Read in other languages:</p>
            <div className="flex flex-wrap gap-2">
              {translations.map((tr, i) => (
                <a key={i} href={`/blog/${tr.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-muted)] text-sm hover:border-[var(--color-primary)] hover:text-[var(--color-text)] transition-colors" data-testid={`translation-${tr.language}`}>
                  <span>{tr.flag}</span>
                  <span>{tr.language_name}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="prose prose-invert prose-lg max-w-none text-[var(--color-text)] leading-relaxed
          prose-h2:font-['Cabinet_Grotesk'] prose-h2:text-white prose-h2:tracking-tight
          prose-h3:font-['Cabinet_Grotesk'] prose-h3:text-white
          prose-a:text-[#a855f7] prose-a:no-underline hover:prose-a:underline
          prose-strong:text-white prose-li:text-[var(--color-text-muted)]"
          dangerouslySetInnerHTML={{ __html: post.content }}
          data-testid="post-content"
        />

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t border-[var(--color-border)]">
            {post.tags.map((tag, i) => (
              <span key={i} className="flex items-center gap-1 px-3 py-1 bg-[var(--color-surface-alt)] text-[var(--color-text-muted)] text-sm border border-[var(--color-border)]">
                <Tag className="w-3 h-3" />{tag}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-8 bg-[var(--color-surface)] border border-[var(--color-border)] text-center">
          <h3 className="text-xl font-black font-['Cabinet_Grotesk'] mb-2">Is your website visible to AI?</h3>
          <p className="text-[var(--color-text-muted)] text-sm mb-4">Scan your website for free and find out if ChatGPT, Perplexity, and Gemini cite your site.</p>
          <a href="/" className="inline-block px-8 py-3 bg-[#a855f7] text-white font-bold uppercase tracking-wider hover:bg-purple-600 transition-colors">
            SCAN FREE
          </a>
        </div>
      </article>
    </div>
  );
}
